import { TabbedModal } from '../tabbed-modal.js';
import { t } from '../../services/i18n-admin.js';
import { icon } from '../../utils/icon.js';

/**
 * ContratoModalTemplate — HTML de los 8 paneles del modal de contrato.
 *
 * Solo plantillas inline. La lógica vive en contrato-modal.js (ContratoModal).
 * Mismos IDs de inputs — separación template/lógica clara.
 *
 * Mapa de paneles:
 *   1. panelComisiones()    porcentaje, base, netwin negativo.
 *   2. panelWallet()        nivel de agrupación de saldo.
 *   3. panelIncentivos()    promociones scoped al nodo.
 *   4. panelObligaciones()  impuestos/fees/deducciones (radio-cards visuales).
 *   5. panelAdelantos()     límites mensuales de adelantos sub-op.
 *   6. panelFondo()         fondo de maniobra (límites + responsables).
 *   7. panelConformidad()   políticas de aprobación + voucher.
 *   8. panelPrepago()       config prepago si aplica.
 *   9. panelCompliance()    KYC/AML del contrato.
 *
 * Componente reusado: TabbedModal (uniformidad de tabs).
 */

/**
 * @param {Object} [opts]
 * @param {boolean} [opts.soloPromotor] Si true, muestra SOLO las pestañas que
 *   aplican a un Promotor Digital (que no mueve dinero): únicamente Comisiones.
 *   Las demás (Wallet, Adelantos, Fondo, Prepago, Compliance, etc.) son de un
 *   nodo operativo y no le sirven — se ocultan. Solo presentación: el motor de
 *   contrato no cambia, el promotor simplemente no ve lo que no usa.
 */
export function contratoModal(opts = {}) {
        const soloPromotor = !!opts.soloPromotor;
        // `promotor: true` marca las pestañas que un Promotor Digital SÍ usa.
        const TODAS_LAS_TABS = [
            { id: 'obligaciones', icon: 'receipt', title: t('Obligaciones'), desc: t('Impuestos y fees') },
            { id: 'comisiones', icon: 'percent', title: t('Comisiones'), desc: t('Base y porcentaje'), promotor: true },
            { id: 'wallet', icon: 'wallet', title: t('Wallet'), desc: t('Nivel de saldo') },
            { id: 'adelantos', icon: 'banknote', title: t('Adelantos'), desc: t('Limites') },
            { id: 'fondo', icon: 'piggy-bank', title: t('Fondo'), desc: t('Maniobra') },
            { id: 'incentivos', icon: 'gem', title: t('Incentivos'), desc: t('Comerciales') },
            { id: 'conformidad', icon: 'check-circle', title: t('Conformidad'), desc: t('Aprobacion') },
            { id: 'prepago', icon: 'shield', title: t('Prepago y Pagos'), desc: t('Crédito y umbrales') },
            { id: 'compliance', icon: 'shield-check', title: t('Compliance'), desc: t('Validación regulatoria') },
        ];
        const CONTRATO_TABS = soloPromotor ? TODAS_LAS_TABS.filter(t => t.promotor) : TODAS_LAS_TABS;

        const herenciaIndicator = '<div id="contratoHerenciaIndicator" style="display:none; padding:8px 12px; background:var(--bg-tertiary); border-radius:6px; margin-bottom:12px; font-size:13px;"></div>';

        // Solo armamos los paneles de las tabs visibles (en modo promotor, solo
        // comisiones). Evita inyectar HTML de paneles que el promotor no ve.
        const TODOS_LOS_PANELS = {
            obligaciones: herenciaIndicator + panelObligaciones(),
            comisiones:   herenciaIndicator + panelComisiones(),
            wallet:       panelWallet(),
            adelantos:    panelAdelantos(),
            fondo:        panelFondo(),
            incentivos:   panelIncentivos(),
            conformidad:  panelConformidad(),
            prepago:      panelPrepago(),
            compliance:   panelCompliance(),
        };
        const panels = {};
        for (let i = 0; i < CONTRATO_TABS.length; i++) {
            const id = CONTRATO_TABS[i].id;
            panels[id] = TODOS_LOS_PANELS[id];
        }

        const tabbedBody = TabbedModal.render({
            tabs: CONTRATO_TABS,
            panels,
            defaultTab: soloPromotor ? 'comisiones' : 'obligaciones',
            modalId: 'modalContrato',
        });

        return `
    <div class="modal" id="modalContrato" style="display:none; max-width:650px;">
      <div class="modal-header">
        <h2 id="modalContratoTitle">${t('Contrato')}</h2>
        <button class="btn-icon" data-action="app:closeModal">
          ${icon('x')}
        </button>
      </div>
      <div class="modal-body">
        ${tabbedBody}
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" data-action="app:closeModal">${t('Cancelar')}</button>
        <button class="btn btn-primary" id="contratoSaveBtn" data-action="contrato:save">
          ${icon('check')}
          ${t('Guardar')}
        </button>
      </div>
    </div>`;
}

function panelComisiones() {
        return `
          
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Comision (%)')}</label>
                <input type="number" class="form-input" id="contratoComisionPct" min="0" max="100" step="0.1" value="0" data-action="contrato:toggleComisionCeroHint">
                <div class="form-hint">${t('Porcentaje que recibe esta entidad')}</div>
                <div class="form-hint" id="contratoComisionCeroHint" style="display:none; color:var(--text-muted, #888);">${t('Sin comisión propia: esta entidad no cobra comisión; el margen queda para el nivel superior que sí la pactó.')}</div>
              </div>
              <div class="form-group">
                <label class="form-label">${t('Base de Calculo')}</label>
                <select class="form-select" id="contratoComisionBase">
                  <option value="ggr">${t('Ganancia de la casa (lo apostado menos lo pagado)')}</option>
                  <option value="recarga_jugada">${t('Dinero recién recargado que se juega primero')}</option>
                </select>
              </div>
            </div>
            <hr style="margin:16px 0; border-color:var(--border-color);">
            <details class="form-group" style="margin-bottom:16px;">
              <summary style="cursor:pointer; color:var(--text-secondary); font-size:0.9em;">${t('Comisión por canal (opcional)')}</summary>
              <div class="form-hint" style="margin:8px 0;">${t('Si un canal de venta debe cobrar distinto, indica su % aquí. Vacío = usa la comisión general de arriba.')}</div>
              ${['wallet', 'ticket'].map(canal => `
              <div id="canalBloque_${canal}">
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">${t('Comisión')} ${canal === 'wallet' ? t('Wallet') : t('Ticket')} (%)</label>
                  <input type="number" class="form-input" id="contratoComision_${canal}_pct" min="0" max="100" step="0.1" placeholder="${t('general')}">
                </div>
                <div class="form-group">
                  <label class="form-label">${t('Base')}</label>
                  <select class="form-select" id="contratoComision_${canal}_base">
                    <option value="ggr">${t('GGR')}</option>
                    <option value="recarga_jugada">${t('Recarga Jugada')}</option>
                  </select>
                </div>
              </div>
              <div class="form-group" style="margin-top:-8px; margin-bottom:8px;">
                <label style="display:flex; align-items:center; gap:6px; cursor:pointer; font-size:0.85em; color:var(--text-secondary);">
                  <input type="checkbox" id="contratoComision_${canal}_sincap">
                  ${t('Pagar comisión completa aunque la ganancia de esta rama sea menor')}
                </label>
                <div class="form-hint" style="color:var(--color-peligro, #c0392b); display:none;" id="contratoComision_${canal}_sincap_aviso">
                  ${t('Peligro: con esta opción la casa puede pagar más comisión que la ganancia (GGR) de esta rama. Usar solo si se garantiza la comisión sobre la venta.')}
                </div>
              </div>
              </div>`).join('')}
            </details>
            <hr style="margin:16px 0; border-color:var(--border-color);">
            <div class="form-group">
              <label class="form-label">${t('Periodicidad de pago de comisión')}</label>
              <select class="form-select" id="contratoPeriodicidadTipo" data-action="contrato:togglePeriodicidad">
                <option value="">${t('Sin definir (hereda del padre)')}</option>
                <option value="semanal">${t('Semanal (cada 7 días)')}</option>
                <option value="quincenal">${t('Quincenal (cada 15 días)')}</option>
                <option value="mensual">${t('Mensual (cada 30 días)')}</option>
                <option value="dias_fijos">${t('Días concretos del mes')}</option>
              </select>
              <div class="form-hint">${t('Cada cuánto se le paga la comisión a esta entidad')}</div>
            </div>
            <div class="form-group" id="periodicidadDiasFijosGroup" style="display:none;">
              <label class="form-label">${t('Días del mes')}</label>
              <input type="text" class="form-input" id="contratoPeriodicidadDiasFijos" placeholder="${t('ej: 1,15')}">
              <div class="form-hint">${t('Días fijos del mes en que se paga (separados por coma, 1-31)')}</div>
            </div>
            <hr style="margin:16px 0; border-color:var(--border-color);">
            <div class="form-group">
              <label class="form-label">${t('Si un período cierra en negativo')}</label>
              <select class="form-select" id="contratoAmortizacionPreset" data-action="contrato:toggleAmortizacionCuotas">
                <option value="0">${t('La casa cubre (sin preocupaciones)')}</option>
                <option value="cuotas">${t('Se recupera de a poco (cuotas)')}</option>
                <option value="100" selected>${t('El nodo paga toda la deuda de una vez')}</option>
              </select>
              <div class="form-hint" id="contratoAmortizacionHint">${t('Una deuda se reparte como una ganancia. Esto decide a qué ritmo se salda.')}</div>
            </div>
            <div class="form-group" id="amortizacionCuotasGroup" style="display:none;">
              <label class="form-label">${t('% que se salda por período')}</label>
              <input type="number" class="form-input" id="contratoPorcentajeAmortizacion" min="1" max="99" step="1" value="10">
              <div class="form-hint">${t('Ej.: 10% → si el nodo debe $100, cada cierre le recupera $10 y el resto sigue pendiente.')}</div>
              <div class="form-hint" style="opacity:0.7;">${t('Orientativo de la industria (no obligatorio): comisión suele rondar 25-35% con cuotas, 20-30% si la casa cubre, hasta 40-45% si el nodo paga toda su deuda de una vez.')}</div>
            </div>
            <div class="form-group" style="margin-top:16px;">
              <label class="form-label">${t('Notas')}</label>
              <textarea class="form-input" id="contratoNotas" rows="2" placeholder="${t('Observaciones del contrato...')}"></textarea>
            </div>
`;
}

function panelWallet() {
        return `
          
            <div class="form-group">
              <label class="form-label">${t('Alcance de operaciones (recargas, tickets y cobros)')}</label>
              <select class="form-select" id="contratoNivelWallet">
                <option value="">${t('Sin definir (hereda del padre)')}</option>
                <option value="punto_venta">${t('Solo en este punto de venta')}</option>
                <option value="representante">${t('En cualquier punto del mismo representante')}</option>
                <option value="operador">${t('En cualquier punto del mismo operador')}</option>
              </select>
              <div class="form-hint">${t('Ej.: "Mismo operador" → una recarga hecha en Lima se puede usar para jugar en Arequipa.')}</div>
            </div>
            <div style="padding:16px; background:var(--bg-tertiary); border-radius:8px; margin-top:16px; line-height:1.6;">
              <strong style="color:var(--accent-blue);">${t('Donde opera el jugador')}</strong>
              <p style="margin:8px 0 0; color:var(--text-secondary); font-size:13px;">
                ${t('Define en que puntos de venta un jugador puede recargar saldo, comprar tickets y cobrar premios:')}
              </p>
              <ul style="margin:8px 0 0; padding-left:20px; color:var(--text-secondary); font-size:13px;">
                <li><strong>${t('Solo este punto:')}</strong> ${t('El jugador recarga, juega y cobra unicamente en el punto donde se registro. Un ticket vendido aqui solo se cobra aqui.')}</li>
                <li><strong>${t('Mismo representante:')}</strong> ${t('El jugador puede operar en cualquier punto que pertenezca al mismo representante. Ej: un ticket vendido en Sucursal A se puede cobrar en Sucursal B si ambas son del mismo representante.')}</li>
                <li><strong>${t('Mismo operador:')}</strong> ${t('El jugador puede operar en cualquier punto de toda la red del operador. Ej: una recarga hecha en Lima se puede usar para jugar en Arequipa.')}</li>
              </ul>
            </div>
`;
}

function panelIncentivos() {
        return `
          
            <div class="form-hint" style="margin-bottom:8px;">
              ${t('Promociones del motor de Marketing scoped a esta entidad.')}
            </div>
            <div style="display:flex; gap:8px; margin-bottom:12px; flex-wrap:wrap;">
              <button class="btn btn-primary btn-sm" data-action="contrato:abrirCrearPromoScoped">
                ${t('+ Nueva Promocion')}
              </button>
              <button class="btn btn-sm" data-action="contrato:abrirClonarPreset">
                ${t('Desde Plantilla')}
              </button>
            </div>
            <div id="incentivosListContainer">
              <div style="text-align:center; padding:20px; color:var(--text-tertiary);">${t('Cargando...')}</div>
            </div>
`;
}

function panelObligaciones() {
        const operadorOptions = ['>', '>=', '<', '<=', '=']
            .map(op => `<option value="${op}">${op}</option>`)
            .join('');

        // Tarjetas de tipo — icono + etiqueta + valor semántico
        const tipoCards = [
            ['impuesto_estatal', 'landmark', t('Estatal'), t('ISC, IVA, IEPS…')],
            ['impuesto_municipal', 'building-2', t('Municipal'), t('SAT, retención local…')],
            ['fee_plataforma', 'calculator', t('Fee<br>plataforma'), t('Licencia de software')],
            ['regalia', 'gem', t('Regalía'), t('Derecho de marca/IP')],
            ['pozo_comun', 'piggy-bank', t('Pozo<br>común'), t('Jackpot compartido')],
            ['mantenimiento', 'wrench', t('Manteni-<br>miento'), t('Cuota operativa fija')],
            ['otro', 'more-horizontal', t('Otro'), t('Tipo personalizado')],
        ];

        const tipoCardsHtml = tipoCards.map(([valor, icono, etiqueta, descripcion]) => `
          <div class="ob-tipo-card" data-value="${valor}" data-action="contrato:onTipoCardClick" title="${descripcion}"
               style="cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;
                      padding:10px 6px;background:var(--bg-secondary);border:2px solid var(--border-color);border-radius:8px;
                      height:72px;text-align:center;transition:var(--transition);gap:5px;">
            ${icon(icono, '', { style: 'width:20px;height:20px;flex-shrink:0;opacity:0.7;' })}
            <span style="font-size:10px;font-weight:600;line-height:1.25;max-width:100%;overflow-wrap:break-word;hyphens:auto;">${etiqueta}</span>
          </div>`).join('');

        // Bases como radio-cards con descripción
        const basesRadioCards = [
            ['ggr', 'GGR', t('Apostado − pagado. Ej.: jugaron $100, pagaste $60 → base = $40.')],
            ['handle_neto', t('Handle Neto'), t('Apostado − reintegros. Ej.: jugaron $100, $10 de reintegro → base = $90.')],
            ['premio_neto', t('Premio Neto'), t('Pagado − reintegros. Ej.: pagaste $60, $10 de reintegro → base = $50.')],
            ['premio_bruto', t('Premio Bruto'), t('Total pagado en premios. Ej.: pagaste $60 en premios → base = $60.')],
            ['facturacion', t('Facturación'), t('Depósitos − retiros. Ej.: recargaron $100, retiraron $30 → base = $70.')],
            ['handle', 'Handle', t('Total apostado bruto. Ej.: jugaron $100 → base = $100 (sin restar nada).')],
            ['comision', t('Comisión'), t('Sobre la comisión ya calculada. Ej.: la comisión dio $40 → base = $40.')],
        ].map(([v, label, desc], i) => `
          <label class="sorteo-check-item">
            <input type="radio" name="obligacionBase" value="${v}" ${i === 0 ? 'checked' : ''}>
            <div class="sorteo-check-card" style="flex-direction:column;align-items:flex-start;gap:2px;padding:8px 12px;">
              <div style="display:flex;align-items:center;justify-content:space-between;width:100%;">
                <span class="sorteo-name">${label}</span>
                ${icon('check', 'sorteo-check-icon', { style: 'width:14px;height:14px;flex-shrink:0;' })}
              </div>
              <span style="font-size:11px;color:var(--text-tertiary);line-height:1.3;">${desc}</span>
            </div>
          </label>`).join('');

        // Tipo de cálculo como radio-cards con icono
        const calculoTipoCards = [
            ['porcentaje', 'percent', t('Porcentaje'), 'base × %'],
            ['fijo', 'dollar-sign', t('Monto fijo'), t('importe constante')],
            ['mayor_de', 'trending-up', t('Mayor de'), t('% o fijo (el mayor)')],
            ['menor_de', 'scale', t('Menor de'), t('% o fijo (el menor)')],
        ].map(([v, icono, label, sub]) => `
          <div class="ob-calculo-card" data-value="${v}" data-action="contrato:onCalculoCardClick" style="cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:8px 4px;background:var(--bg-tertiary);border:2px solid var(--border-color);border-radius:8px;text-align:center;min-height:54px;transition:var(--transition);">
            ${icon(icono, '', { style: 'width:16px;height:16px;opacity:0.7;margin-bottom:3px;' })}
            <span style="font-size:11px;font-weight:600;line-height:1.2;">${label}</span>
            <span style="font-size:10px;color:var(--text-tertiary);line-height:1.2;">${sub}</span>
          </div>`).join('');

        return `
          
            <div class="form-hint" style="margin-bottom:8px;">
              ${t('Impuestos, fees y deducciones. Se calculan sobre la actividad del nodo que opera (donde se vende).')} ${t('Las heredadas del padre aplican automáticamente — para sobreescribir una, crea una nueva con el mismo código.')}
            </div>
            <button class="btn btn-primary btn-sm" id="obligacionToggleFormBtn" data-action="contrato:toggleObligacionForm" style="margin-bottom:12px;">
              ${t('+ Nueva Obligacion')}
            </button>

            <div id="obligacionFormContainer" style="display:none; background:var(--bg-tertiary); border-radius:10px; margin-bottom:12px; overflow:hidden;">

              <!-- Indicador de pasos -->
              <div id="obligacionWizardSteps" style="display:flex; border-bottom:1px solid var(--border); padding:0 16px;">
                <div class="ob-step-tab active" data-step="1" style="padding:10px 14px; font-size:12px; font-weight:600; cursor:pointer; border-bottom:2px solid transparent; color:var(--text-tertiary); transition:var(--transition);">${t('1 Tipo')}</div>
                <div class="ob-step-tab" data-step="2" style="padding:10px 14px; font-size:12px; font-weight:600; cursor:pointer; border-bottom:2px solid transparent; color:var(--text-tertiary); transition:var(--transition);">${t('2 Base')}</div>
                <div class="ob-step-tab" data-step="3" style="padding:10px 14px; font-size:12px; font-weight:600; cursor:pointer; border-bottom:2px solid transparent; color:var(--text-tertiary); transition:var(--transition);">${t('3 Importe')}</div>
                <div class="ob-step-tab" data-step="4" style="padding:10px 14px; font-size:12px; font-weight:600; cursor:pointer; border-bottom:2px solid transparent; color:var(--text-tertiary); transition:var(--transition);">${t('4 Pago')}</div>
              </div>

              <!-- PASO 1: Tipo + Identificación -->
              <div id="obligacionStep1" class="ob-step-panel" style="padding:16px;">
                <div style="font-size:11px; font-weight:600; color:var(--text-tertiary); text-transform:uppercase; margin-bottom:10px; letter-spacing:0.5px;">${t('Tipo de obligación')}</div>
                <div style="display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-bottom:14px;" id="obligacionTipoCards">
                  ${tipoCardsHtml}
                </div>
                <!-- Input inline para "Otro" -->
                <div id="obligacionTipoOtroBlock" style="display:none; margin-bottom:14px;">
                  <input type="text" class="form-input" id="obligacionTipoOtroTexto" placeholder="${t('Nombre del tipo personalizado')}" maxlength="60">
                </div>

                <div class="form-row" style="gap:12px;">
                  <div class="form-group" style="flex:0.7">
                    <label class="form-label">${t('Código')}</label>
                    <input type="text" class="form-input" id="obligacionCodigo" placeholder="ISC" maxlength="30" style="text-transform:uppercase;">
                  </div>
                  <div class="form-group" style="flex:2">
                    <label class="form-label">${t('Nombre completo')}</label>
                    <input type="text" class="form-input" id="obligacionNombre" placeholder="${t('Impuesto Selectivo al Consumo')}">
                  </div>
                </div>
                <div class="form-group">
                  <label class="form-label">${t('Beneficiario')} <span style="font-weight:400;color:var(--text-tertiary);">${t('(opcional)')}</span></label>
                  <input type="text" class="form-input" id="obligacionBeneficiario" placeholder="${t('SUNAT, municipio, nombre del acreedor…')}">
                </div>
                <!-- Campo hidden que guarda el valor final del tipo -->
                <input type="hidden" id="obligacionTipo" value="impuesto_estatal">

                <div style="display:flex; justify-content:flex-end; margin-top:8px;">
                  <button class="btn btn-primary btn-sm" data-action="contrato:wizardNext">${t('Siguiente')} →</button>
                </div>
              </div>

              <!-- PASO 2: Base de cálculo -->
              <div id="obligacionStep2" class="ob-step-panel" style="display:none; padding:16px;">
                <div style="font-size:11px; font-weight:600; color:var(--text-tertiary); text-transform:uppercase; margin-bottom:10px; letter-spacing:0.5px;">${t('¿Sobre qué métrica se calcula?')}</div>
                <div style="display:flex; flex-direction:column; gap:6px; margin-bottom:12px;">
                  ${basesRadioCards}
                </div>

                <!-- Base neta: flag contextual -->
                <label style="display:flex; align-items:center; gap:8px; cursor:pointer; padding:8px 12px; background:var(--bg-secondary); border-radius:6px; border:1px solid var(--border); margin-bottom:12px;">
                  <input type="checkbox" id="obligacionBaseNeta">
                  <div>
                    <div style="font-size:12px; font-weight:600; color:var(--text-primary);">${t('Base neta')} <span style="color:var(--text-tertiary); font-weight:400;">${t('(descontar deducciones anteriores)')}</span></div>
                    <div style="font-size:11px; color:var(--text-tertiary); margin-top:1px;">${t('Útil para comisiones post-impuestos: la base se reduce con lo ya deducido.')}</div>
                  </div>
                </label>

                <!-- Condición de aplicación -->
                <div style="font-size:11px; font-weight:600; color:var(--text-tertiary); text-transform:uppercase; margin-bottom:8px; letter-spacing:0.5px;">${t('Condición de aplicación')} <span style="font-weight:400;text-transform:none;">${t('(opcional)')}</span></div>
                <div style="display:flex; align-items:center; gap:8px; padding:10px 12px; background:var(--bg-secondary); border-radius:6px; border:1px solid var(--border);">
                  <span style="font-size:12px; color:var(--text-secondary); white-space:nowrap;">${t('Aplicar solo si la base')}</span>
                  <select class="form-select" id="obligacionCondicionOp" style="flex:0 0 64px;">${operadorOptions}</select>
                  <input type="number" class="form-input" id="obligacionCondicionValor" placeholder="${t('vacío = siempre aplica')}" style="flex:1;">
                </div>

                <div style="display:flex; justify-content:space-between; margin-top:14px;">
                  <button class="btn btn-sm" data-action="contrato:wizardPrev">← ${t('Atrás')}</button>
                  <button class="btn btn-primary btn-sm" data-action="contrato:wizardNext">${t('Siguiente')} →</button>
                </div>
              </div>

              <!-- PASO 3: Importe (cómo se calcula el monto) -->
              <div id="obligacionStep3" class="ob-step-panel" style="display:none; padding:16px;">
                <div style="font-size:11px; font-weight:600; color:var(--text-tertiary); text-transform:uppercase; margin-bottom:10px; letter-spacing:0.5px;">${t('¿Cómo se calcula el importe?')}</div>
                <div style="display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-bottom:16px;" id="obligacionCalculoCards">
                  ${calculoTipoCards}
                </div>
                <input type="hidden" id="obligacionCalculoTipo" value="porcentaje">

                <!-- Porcentaje (visible en porcentaje / mayor_de / menor_de) -->
                <div id="obligacionPorcentajeGroup" class="form-group" style="margin-bottom:10px;">
                  <label class="form-label" id="obligacionPorcentajeLabel">${t('Porcentaje')} %</label>
                  <input type="number" class="form-input" id="obligacionPorcentaje" min="0" max="100" step="0.01" value="0">
                </div>

                <!-- Monto fijo (visible en fijo / mayor_de / menor_de) -->
                <div id="obligacionMontoFijoGroup" class="form-group" style="display:none; margin-bottom:10px;">
                  <label class="form-label" id="obligacionMontoFijoLabel">${t('Monto fijo')} <span style="font-weight:400;color:var(--text-tertiary);">${t('(en centavos)')}</span></label>
                  <input type="number" class="form-input" id="obligacionMontoFijo" min="0" value="" placeholder="${t('ej: 5000 centavos')}">
                </div>

                <div style="display:flex; justify-content:space-between; margin-top:14px;">
                  <button class="btn btn-sm" data-action="contrato:wizardPrev">← ${t('Atrás')}</button>
                  <button class="btn btn-primary btn-sm" data-action="contrato:wizardNext">${t('Siguiente')} →</button>
                </div>
              </div>

              <!-- PASO 4: Quién paga + Alcance + Resumen -->
              <div id="obligacionStep4" class="ob-step-panel" style="display:none; padding:16px;">
                <div style="font-size:11px; font-weight:600; color:var(--text-tertiary); text-transform:uppercase; margin-bottom:10px; letter-spacing:0.5px;">${t('¿Quién absorbe este costo?')}</div>
                <div style="display:flex; flex-direction:column; gap:6px; margin-bottom:10px;">
                  <label class="sorteo-check-item">
                    <input type="radio" name="obligacionAbsorbe" value="operacion" checked data-action="contrato:onAbsorbeChange">
                    <div class="sorteo-check-card" style="flex-direction:column;align-items:flex-start;gap:2px;padding:8px 12px;">
                      <div style="display:flex;align-items:center;justify-content:space-between;width:100%;">
                        <span class="sorteo-name">${t('La operación')}</span>
                        ${icon('check', 'sorteo-check-icon', { style: 'width:14px;height:14px;flex-shrink:0;' })}
                      </div>
                      <span style="font-size:11px;color:var(--text-tertiary);">${t('Sale directamente del neto del corte.')}</span>
                    </div>
                  </label>
                  <label class="sorteo-check-item">
                    <input type="radio" name="obligacionAbsorbe" value="jugador" data-action="contrato:onAbsorbeChange">
                    <div class="sorteo-check-card" style="flex-direction:column;align-items:flex-start;gap:2px;padding:8px 12px;">
                      <div style="display:flex;align-items:center;justify-content:space-between;width:100%;">
                        <span class="sorteo-name">${t('El jugador')}</span>
                        ${icon('check', 'sorteo-check-icon', { style: 'width:14px;height:14px;flex-shrink:0;' })}
                      </div>
                      <span style="font-size:11px;color:var(--text-tertiary);">${t('Retención visible en el voucher del premio.')}</span>
                    </div>
                  </label>
                  <label class="sorteo-check-item">
                    <input type="radio" name="obligacionAbsorbe" value="condicional" data-action="contrato:onAbsorbeChange">
                    <div class="sorteo-check-card" style="flex-direction:column;align-items:flex-start;gap:2px;padding:8px 12px;">
                      <div style="display:flex;align-items:center;justify-content:space-between;width:100%;">
                        <span class="sorteo-name">${t('Condicional por umbral')}</span>
                        ${icon('check', 'sorteo-check-icon', { style: 'width:14px;height:14px;flex-shrink:0;' })}
                      </div>
                      <span style="font-size:11px;color:var(--text-tertiary);">${t('Si el premio supera un monto → jugador; si no → operación.')}</span>
                    </div>
                  </label>
                </div>

                <!-- Bloque condicional (solo si se elige "condicional") -->
                <div id="obligacionAbsorbeCondicionalBlock" style="display:none; padding:10px 12px; background:var(--bg-secondary); border-radius:6px; border:1px solid var(--border); margin-bottom:10px;">
                  <div style="font-size:11px; color:var(--text-secondary); margin-bottom:6px;">${t('Si base')}</div>
                  <div style="display:flex; align-items:center; gap:8px;">
                    <select class="form-select" id="obligacionAbsorbeOp" style="flex:0 0 64px;">${operadorOptions}</select>
                    <input type="number" class="form-input" id="obligacionAbsorbeValor" placeholder="${t('umbral en centavos')}" style="flex:1;">
                    <span style="font-size:12px; color:var(--text-tertiary); white-space:nowrap;">${t('→ jugador, si no → operación')}</span>
                  </div>
                </div>

                <!-- Alcance -->
                <div style="font-size:11px; font-weight:600; color:var(--text-tertiary); text-transform:uppercase; margin-bottom:8px; letter-spacing:0.5px;">${t('Alcance')}</div>
                <div style="display:flex; gap:12px; align-items:flex-start; margin-bottom:12px;">
                  <div class="form-group" style="flex:0.5; margin-bottom:0;">
                    <label class="form-label">${t('Orden de evaluación')}</label>
                    <input type="number" class="form-input" id="obligacionOrden" min="1" value="100">
                    <div class="form-hint">${t('Menor = antes. Comisión ≈ 500.')}</div>
                  </div>
                  <div class="form-group" style="flex:1; margin-bottom:0;">
                    <label class="form-label">&nbsp;</label>
                    <label style="display:flex; align-items:center; gap:8px; cursor:pointer; padding:9px 12px; background:var(--bg-secondary); border-radius:6px; border:1px solid var(--border); margin-top:2px;">
                      <input type="checkbox" id="obligacionHeredable" checked>
                      <div>
                        <div style="font-size:12px; font-weight:600; color:var(--text-primary);">${t('Heredable')}</div>
                        <div style="font-size:11px; color:var(--text-tertiary);">${t('Aplica también a nodos hijos.')}</div>
                      </div>
                    </label>
                  </div>
                </div>

                <!-- Resumen antes de guardar -->
                <div id="obligacionResumen" style="padding:10px 12px; background:rgba(59,130,246,0.08); border:1px solid rgba(59,130,246,0.25); border-radius:6px; margin-bottom:12px; font-size:12px; color:var(--text-secondary); line-height:1.6;">
                  <!-- Rellenado dinámicamente por _actualizarResumenObligacion() -->
                </div>

                <div style="display:flex; justify-content:space-between;">
                  <button class="btn btn-sm" data-action="contrato:wizardPrev">← ${t('Atrás')}</button>
                  <div style="display:flex; gap:8px;">
                    <button class="btn btn-sm" data-action="contrato:toggleObligacionForm">${t('Cancelar')}</button>
                    <button class="btn btn-primary btn-sm" data-action="contrato:crearObligacion">
                      ${icon('check', '', { style: 'width:14px;height:14px;vertical-align:middle;margin-right:4px;' })}
                      ${t('Guardar obligación')}
                    </button>
                  </div>
                </div>
              </div>

            </div>

            <div id="obligacionesListContainer">
              <div style="text-align:center; padding:20px; color:var(--text-tertiary);">${t('Cargando...')}</div>
            </div>
`;
}

function panelAdelantos() {
        return `
          
            <div class="form-group">
              <label class="form-label">${t('Limite de Adelanto Mensual')}</label>
              <input type="number" class="form-input" id="contratoAdelantoLimite" min="0" placeholder="${t('Sin limite')}">
              <div class="form-hint">${t('Monto maximo en centavos que puede solicitar por mes (0 = sin limite)')}</div>
            </div>
            <div style="padding:16px; background:var(--bg-tertiary); border-radius:8px; margin-top:16px; line-height:1.6;">
              <strong style="color:var(--accent-green);">${t('Adelantos')}</strong>
              <p style="margin:8px 0 0; color:var(--text-secondary); font-size:13px;">
                ${t('Prestamos entre entidades de la jerarquia. El hijo puede solicitar adelantos al padre, los cuales se descuentan de futuras liquidaciones.')}
              </p>
            </div>
`;
}

function panelFondo() {
        return `
          
            <div class="form-group">
              <label class="form-label">${t('Limite de Fondo de Maniobra')}</label>
              <input type="number" class="form-input" id="contratoFondoLimite" min="0" placeholder="${t('Sin limite')}">
              <div class="form-hint">${t('Monto maximo en centavos para operar en negativo')}</div>
            </div>
            <div style="padding:16px; background:var(--bg-tertiary); border-radius:8px; margin-top:16px; line-height:1.6;">
              <strong style="color:var(--accent-orange);">${t('Fondo de Maniobra')}</strong>
              <p style="margin:8px 0 0; color:var(--text-secondary); font-size:13px;">
                ${t('Permite a la entidad operar temporalmente con saldo negativo. Util para PVs que necesitan pagar premios antes de recaudar.')}
              </p>
            </div>
`;
}

function panelConformidad() {
        return `
          
            <div class="form-group">
              <label class="form-label">${t('Politica de Conformidad')}</label>
              <select class="form-select" id="contratoConformidadPolitica" data-action="contrato:toggleConformidadPlazo">
                <option value="tiempo_real">${t('Tiempo Real (requiere confirmacion inmediata)')}</option>
                <option value="plazo_horas">${t('Plazo en Horas')}</option>
                <option value="automatico">${t('Automatico (se acepta sin confirmar)')}</option>
              </select>
            </div>
            <div class="form-group" id="conformidadPlazoGroup" style="display:none;">
              <label class="form-label">${t('Plazo (horas)')}</label>
              <input type="number" class="form-input" id="contratoConformidadPlazo" min="1" max="168" value="24">
              <div class="form-hint">${t('Horas para confirmar antes de aceptacion automatica')}</div>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Canal de Notificacion')}</label>
              <select class="form-select" id="contratoConformidadCanal">
                <option value="sistema">${t('Solo sistema (panel admin)')}</option>
                <option value="whatsapp">${t('Solo WhatsApp (deeplink)')}</option>
                <option value="ambos">${t('Sistema + WhatsApp')}</option>
              </select>
              <div class="form-hint">${t('Como se notifica al receptor cuando hay un corte o liquidacion pendiente')}</div>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Telefono WhatsApp del Receptor')}</label>
              <input type="text" class="form-input" id="contratoConformidadTelefonoWhatsapp"
                placeholder="${t('51999888777 (sin + ni espacios)')}" maxlength="20">
              <div class="form-hint">${t('Numero internacional sin +. Genera el deeplink wa.me para notificaciones.')}</div>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Nodo que Recibe el Neto')} (liquidacion_nodo_id)</label>
              <select class="form-select" id="contratoLiquidacionNodoId">
                <option value="">${t('Padre directo (por defecto)')}</option>
              </select>
              <div class="form-hint">${t('A quien se liquida el neto del corte. Vacio = padre directo en el arbol.')}</div>
            </div>
            <div style="padding:16px; background:var(--bg-tertiary); border-radius:8px; margin-top:16px; line-height:1.6;">
              <strong style="color:var(--accent-blue);">${t('Conformidad')}</strong>
              <p style="margin:8px 0 0; color:var(--text-secondary); font-size:13px;">
                ${t('Define como se aprueban los cortes y liquidaciones:')}
              </p>
              <ul style="margin:8px 0 0; padding-left:20px; color:var(--text-secondary); font-size:13px;">
                <li><strong>${t('Tiempo Real:')}</strong> ${t('Requiere aprobacion explicita')}</li>
                <li><strong>${t('Plazo:')}</strong> ${t('Si no se rechaza en N horas, se acepta')}</li>
                <li><strong>${t('Automatico:')}</strong> ${t('Se acepta inmediatamente')}</li>
              </ul>
            </div>
`;
}

function panelPrepago() {
        return `
            <div class="form-group">
              <label class="form-label" style="display:flex; align-items:center; gap:8px;">
                <input type="checkbox" id="contratoPrepago" style="width:18px; height:18px;">
                ${t('Habilitar modelo prepago')}
              </label>
              <div class="form-hint">${t('El agente opera con credito asignado. Se bloquean ventas cuando el saldo operativo llega a 0.')}</div>
            </div>
            <div id="prepagoConfigGroup" style="display:none;">
              <div style="padding:12px 16px; background:var(--bg-tertiary); border-radius:8px; margin-bottom:16px; border-left:3px solid var(--accent-blue);">
                <strong style="color:var(--accent-blue); font-size:13px;">${t('Credito del Agente')}</strong>
                <p style="margin:4px 0 0; color:var(--text-secondary); font-size:12px;">
                  ${t('El agente recibe un credito inicial y opera hasta agotarlo. Las ventas consumen credito, los cobros de premios y retiros lo reponen.')}
                </p>
              </div>
              <div class="form-group">
                <label class="form-label">${t('Limite de Credito (centimos)')}</label>
                <input type="number" class="form-input" id="contratoLimiteCredito" min="0" placeholder="${t('Ej: 500000 (5,000.00)')}">
                <div class="form-hint">${t('Tope maximo de credito. Vacio = sin limite.')}</div>
              </div>
            </div>

            <hr style="border:none; border-top:1px solid var(--border-glass); margin:20px 0;">

            <div style="padding:12px 16px; background:var(--bg-tertiary); border-radius:8px; margin-bottom:16px; border-left:3px solid var(--accent-warning);">
              <strong style="color:var(--accent-warning); font-size:13px;">${t('Umbral de Pago Directo')}</strong>
              <p style="margin:4px 0 0; color:var(--text-secondary); font-size:12px;">
                ${t('Pagos (premios de ticket y retiros de wallet) por encima del umbral los cubre otro nodo de la red, no el agente. Aplica con o sin modelo prepago.')}
              </p>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Umbral de Pago Directo (centimos)')}</label>
              <input type="number" class="form-input" id="contratoUmbralPremio" min="1" placeholder="${t('Ej: 100000 (1,000.00)')}">
              <div class="form-hint">${t('Pagos por encima de este monto se delegan automaticamente. Vacio = el agente paga todo.')}</div>
            </div>
            <div class="form-group" id="umbralPagoNodoGroup" style="display:none;">
              <label class="form-label">${t('Quien cubre los pagos delegados')}</label>
              <select class="form-input" id="contratoUmbralPagoNodoId">
                <option value="">${t('Central (operador raiz)')}</option>
              </select>
              <div class="form-hint">${t('Por defecto la central. Se puede especificar un nodo intermedio (representante, sub-operador).')}</div>
            </div>

            <div style="padding:12px 16px; background:var(--bg-tertiary); border-radius:8px; margin-top:16px; line-height:1.5;">
              <strong style="color:var(--text-secondary); font-size:12px;">${t('Niveles de saldo operativo')}</strong>
              <ul style="margin:6px 0 0; padding-left:18px; color:var(--text-secondary); font-size:12px;">
                <li><strong>&gt; 25%:</strong> ${t('Operacion normal')}</li>
                <li><strong>10-25%:</strong> ${t('Alerta amarilla en caja')}</li>
                <li><strong>&lt; 10%:</strong> ${t('Alerta critica (rojo)')}</li>
                <li><strong>= 0:</strong> ${t('Caja bloqueada (solo cobra premios)')}</li>
              </ul>
            </div>
`;
}

function panelCompliance() {
    return `
        <div style="padding:12px 16px; background:var(--bg-tertiary); border-radius:8px; margin-bottom:16px; border-left:3px solid var(--accent-warning);">
          <strong style="color:var(--accent-warning); font-size:13px;">${t('Validación de mayoría de edad por cajero')}</strong>
          <p style="margin:4px 0 0; color:var(--text-secondary); font-size:12px; line-height:1.5;">
            ${t('Activar cuando el canal de entrada NO presume mayoría de edad bancaria (cupón físico, QR mostrador, lluvia QR TV). Onboardings desde esos canales requerirán PIN one-time entregado cara a cara por el cajero.')}
          </p>
          <p style="margin:4px 0 0; color:var(--text-secondary); font-size:12px; line-height:1.5;">
            ${t('Pasarela bancaria (PIX, Yape, tarjeta) ignora este flag — el banco ya validó mayoría al onboarding. Casino/sala con control en puerta debe quedar')} <strong>${t('desactivado')}</strong>.
          </p>
        </div>
        <div class="form-group">
          <label class="form-label" style="display:flex; align-items:center; gap:8px;">
            <input type="checkbox" id="contratoMayoriaEdadValidadaPorCajero" style="width:18px; height:18px;">
            ${t('Requerir validación cara a cara en onboardings sin canal bancario')}
          </label>
          <div class="form-hint">
            ${t('Hereda fractal por contrato. Si este nodo no setea, hereda del padre vigente.')}
          </div>
        </div>
`;
}
